// Exact animation curves written by assetconv (`.wanim`, docs/platform/AssetConv.md) and their
// evaluation: Maya animEngine curves as in CurveEvaluationCache::EvaluateAt 0x11ee0c0, transform
// cores recomputed with FastTrig::SinCos0 0x125ae30 (docs/re/formats/graphics.md 3.2, 6.3).
// Port of re/tools/formats/graphics.py (AnimCurve, fast_sincos, rotation_matrix, local_matrix).
// No dependencies: usable by the client as well.

export const KIND_CORE = 0, KIND_NODE = 1, KIND_UNRESOLVED = 2, KIND_UNIFORM = 3;
export const CORE_CLASSES = [null, "XTransform", "XPivotTransform", "XFullPivotTransform", "XJointTransform", "XMatrix"];
export const ROTATE_ORDERS = ["kRotateOrderXYZ", "kRotateOrderYZX", "kRotateOrderZXY", "kRotateOrderXZY", "kRotateOrderYXZ", "kRotateOrderZYX"];

export function parseWanim(buf) {
  const dv = new DataView(buf);
  const td = new TextDecoder();
  let o = 0;
  const u8 = () => dv.getUint8(o++);
  const u16 = () => { const v = dv.getUint16(o, true); o += 2; return v; };
  const u32 = () => { const v = dv.getUint32(o, true); o += 4; return v; };
  const i32 = () => { const v = dv.getInt32(o, true); o += 4; return v; };
  const f32 = () => { const v = dv.getFloat32(o, true); o += 4; return v; };
  const str = () => { const n = u16(); const s = td.decode(new Uint8Array(buf, o, n)); o += n; return s; };
  if (td.decode(new Uint8Array(buf, 0, 4)) !== "WANM") throw new Error("not a .wanim file");
  o = 4;
  const version = u32();
  if (version !== 1) throw new Error("wanim version " + version);
  const attrs = [];
  for (let n = u32(), i = 0; i < n; i++) {
    attrs.push({ target: i32(), field: u8(), sub: u8(), flags: u16(), kind: u8(), core: u8(), path: str() });
  }
  const clips = [];
  for (let n = u32(), i = 0; i < n; i++) {
    const duration = f32(), name = str(), channels = [];
    for (let m = u32(), j = 0; j < m; j++) {
      const attr = u16(), bits = u8(), pre = u8(), post = u8(), nk = u32();
      const keys = new Float32Array(buf.slice(o, o + nk * 24));
      o += nk * 24;
      channels.push({ attr, mustContribute: !!(bits & 1), linear: !!(bits & 2), isStatic: !!(bits & 4), weighted: !!(bits & 8), pre, post, keys });
    }
    clips.push({ duration, name, channels });
  }
  return { version, attrs, clips };
}

const KMAXTAN = 5729578.0; // DAT_01605ae0: slope used when a tangent has x == 0
const THIRD = 1.0 / 3.0;

function bezierEval(x, y, t) {
  const span = x[3] - x[0];
  if (span === 0) return y[0];
  const x1 = Math.min(Math.max((x[1] - x[0]) / span, 0), 1), x2 = Math.min(Math.max((x[2] - x[0]) / span, 0), 1);
  const u = (t - x[0]) / span;
  const bx = s => { const a = 1 - s; return 3 * a * a * s * x1 + 3 * a * s * s * x2 + s * s * s; };
  let lo = 0, hi = 1;
  for (let i = 0; i < 60; i++) { const mid = 0.5 * (lo + hi); if (bx(mid) < u) lo = mid; else hi = mid; }
  const s = 0.5 * (lo + hi), a = 1 - s;
  return a * a * a * y[0] + 3 * a * a * s * y[1] + 3 * a * s * s * y[2] + s * s * s * y[3];
}

/// One channel: keys (time, value, inTanX, inTanY, outTanX, outTanY) per key.
export class Curve {
  constructor(ch) {
    this.k = ch.keys; this.n = ch.keys.length / 6;
    this.isStatic = ch.isStatic; this.weighted = ch.weighted; this.pre = ch.pre; this.post = ch.post;
  }
  t(i) { return this.k[6 * i]; }
  v(i) { return this.k[6 * i + 1]; }
  find(t) {
    let lo = 0, hi = this.n - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1, kt = this.t(mid);
      if (t < kt) hi = mid - 1; else if (t <= kt) return [true, mid]; else lo = mid + 1;
    }
    return [false, lo];
  }
  segment(i, t) {
    const k = this.k, a = 6 * (i - 1), b = 6 * i;
    if (k[a + 4] === 0 && k[a + 5] === 0) return k[a + 1]; // step: out tangent (0, 0)
    const x = [k[a], k[a] + k[a + 4] * THIRD, k[b] - k[b + 2] * THIRD, k[b]];
    const y = [k[a + 1], k[a + 1] + k[a + 5] * THIRD, k[b + 1] - k[b + 3] * THIRD, k[b + 1]];
    if (!this.weighted) { // EngineHermiteCreate / Evaluate
      const dx = x[3] - x[0], dy = y[3] - y[0];
      const m1 = x[1] !== x[0] ? (y[1] - y[0]) / (x[1] - x[0]) : KMAXTAN;
      const m2 = x[3] !== x[2] ? (y[3] - y[2]) / (x[3] - x[2]) : KMAXTAN;
      const len = 1.0 / (dx * dx), d1 = dx * m1, d2 = dx * m2;
      const c0 = (d1 + d2 - dy - dy) * len / dx, c1 = (dy + dy + dy - d1 - d1 - d2) * len;
      const u = t - x[0];
      return u * (u * (u * c0 + c1) + m1) + y[0];
    }
    return bezierEval(x, y, t);
  }
  inside(t) {
    if (this.isStatic) return this.v(0);
    const [exact, i] = this.find(t);
    if (exact || i === 0) return this.v(i);
    if (i === this.n) return this.v(this.n - 1);
    return this.segment(i, t);
  }
  evaluate(t) {
    if (!this.n) return 0;
    const t0 = this.t(0), t1 = this.t(this.n - 1);
    if (this.pre !== 0 && t < t0) return this.infinity(t, true);
    if (this.post !== 0 && t > t1) return this.infinity(t, false);
    return this.inside(t);
  }
  infinity(t, pre) { // EvaluateInfinities 0x11edc90
    const k = this.k, n = this.n, t0 = this.t(0), t1 = this.t(n - 1), rng = t1 - t0;
    if (rng === 0) return this.v(0);
    const q = ((t > t1) ? (t - t1) : (t - t0)) / rng;
    const whole = Math.abs(Math.trunc(q)), frac = Math.abs(q - Math.trunc(q));
    let tt = rng * frac;
    const cycles = 1 + whole, mode = pre ? this.pre : this.post;
    if (mode === 1) { // linear
      if (pre) return k[2] !== 0 ? this.v(0) - ((t0 - t) * k[3]) / k[2] : this.v(0);
      const b = 6 * (n - 1);
      return k[b + 4] !== 0 ? k[b + 1] + (k[b + 5] * (t - t1)) / k[b + 4] : k[b + 1];
    }
    if (mode === 4) { // oscillate
      const odd = (cycles * 0.5) % 1 !== 0;
      tt = pre ? (odd ? tt + t0 : t1 - tt) : (odd ? t1 - tt : tt + t0);
    } else tt = pre ? t1 - tt : tt + t0; // cycle / cycle relative
    let v = this.inside(tt);
    if (mode === 3) { const off = this.v(n - 1) - this.v(0); v = pre ? v - off * cycles : v + off * cycles; }
    return v;
  }
}

// FastTrig::SinCos0 0x125ae30 (XMath/FastTrig.cpp:239)
const fr = Math.fround;
const FT_ADD = 3.1415927410125732, FT_MUL = 10430.3779296875, FT_SCL = 9.58738019107841e-05;
const S3 = 0.00761000020429492, S2 = 0.1660500019788742, C2 = 0.037050001323223114, C1 = 0.4966999888420105;
export function fastSinCos(v) {
  const u = (Math.trunc(fr((fr(v) + FT_ADD) * FT_MUL)) & 0xffff) - 0x8000;
  const i = u < 0 ? -u : u;
  let s, c;
  if (i < 0x4000) {
    const x = fr(i * FT_SCL), x2 = fr(x * x);
    s = fr(x * fr(fr(fr(S3 * x2) - S2) * x2 + 1.0));
    c = fr(fr(fr(C2 * x2) - C1) * x2 + 1.0);
  } else {
    const x = fr((i - 0x4000) * FT_SCL), x2 = fr(x * x);
    s = fr(fr(fr(C2 * x2) - C1) * x2 + 1.0);
    c = -fr(x * fr(x2 * fr(fr(S3 * x2) - S2) + 1.0));
  }
  return [u < 0 ? -s : s, c];
}

/// XomBuildRotationMatrix 0x11fd4c0: 3x3 row-vector rotation, Euler radians.
export function rotationMatrix(r, order) {
  const [sx, cx] = fastSinCos(r[0]), [sy, cy] = fastSinCos(r[1]), [sz, cz] = fastSinCos(r[2]);
  switch (order) {
    case 0: return [[cz * cy, cy * sz, -sy], [cz * sy * sx - sz * cx, sy * sx * sz + cz * cx, cy * sx], [sz * sx + cz * sy * cx, sy * cx * sz - sx * cz, cx * cy]];
    case 1: return [[cz * cy, cx * sz * cy + sx * sy, sz * sx * cy - cx * sy], [-sz, cz * cx, cz * sx], [sy * cz, cx * sy * sz - sx * cy, cx * cy + sz * sx * sy]];
    case 2: return [[sx * sy * sz + cz * cy, sz * cx, sx * sz * cy - cz * sy], [sx * sy * cz - cy * sz, cz * cx, sz * sy + cz * cy * sx], [sy * cx, -sx, cx * cy]];
    case 3: return [[cz * cy, sz, -cz * sy], [-cx * sz * cy + sy * sx, cz * cx, cy * sx + sz * sy * cx], [sy * cx + sz * sx * cy, -cz * sx, -sx * sy * sz + cx * cy]];
    case 4: return [[cx * cy - sx * sy * sz, sx * sy * cz + cy * sx, -sy * cx], [-cx * sz, cx * cz, sx], [sx * sz * cy + sy * cz, sz * sy - cz * cy * sx, cx * cy]];
    case 5: return [[cz * cy, cz * sx * sy + cx * sz, sz * sx - cz * cx * sy], [-cy * sz, cz * cx - sx * sy * sz, cz * sx + cx * sy * sz], [sy, -sx * cy, cx * cy]];
  }
  throw new Error("rotate order " + order);
}

const mul3 = (a, b) => a.map(r => [0, 1, 2].map(j => r[0] * b[0][j] + r[1] * b[1][j] + r[2] * b[2][j]));
const vecMul3 = (v, m) => [0, 1, 2].map(j => v[0] * m[0][j] + v[1] * m[1][j] + v[2] * m[2][j]);

/// Local row-vector matrix of a transform core from its fields (XomAction*Transform, 3.2):
/// returns the 4x3 rows [[r0], [r1], [r2], [t]].
export function localMatrix(core, f) {
  const order = typeof f.RotateOrder === "string" ? Math.max(0, ROTATE_ORDERS.indexOf(f.RotateOrder)) : (f.RotateOrder || 0);
  const S = f.Scale, T = f.Translate, R = rotationMatrix(f.Rotate, order);
  let L, t;
  if (core === "XJointTransform") {
    const RA = rotationMatrix(f.RotateAxis, 0), JO = rotationMatrix(f.JointOrientation, 0);
    L = mul3(mul3(RA, R), JO).map((r, i) => r.map(x => x * S[i]));
    t = T.slice();
  } else {
    L = R.map((r, i) => r.map(x => x * S[i]));
    if (core === "XTransform") t = T.slice();
    else {
      const Rp = f.RotatePivot, Sp = f.ScalePivot;
      const u = [0, 1, 2].map(k => (1 - S[k]) * Sp[k] + (core === "XFullPivotTransform" ? f.ScalePivotTrans[k] : 0) - Rp[k]);
      const uR = vecMul3(u, R);
      t = [0, 1, 2].map(k => uR[k] + Rp[k] + (core === "XFullPivotTransform" ? f.RotatePivotTrans[k] : 0) + T[k]);
    }
  }
  return [L[0], L[1], L[2], t];
}

/// Field name of an animated core field index (XContainerClass::GetEnumField of the core class).
export function coreFieldName(core, index) {
  const base = ["Matrix", "Flags", "Translate", "Rotate", "Scale", "RotateOrder", null, null, "RotatePivotTrans", "ScalePivotTrans"][index];
  if (index === 6) return core === "XJointTransform" ? "RotateAxis" : "RotatePivot";
  if (index === 7) return core === "XJointTransform" ? "JointOrientation" : "ScalePivot";
  return base;
}

/// Binds one clip: returns { tracks: [{ target, core, fields: {name: {sub: Curve}} }], uniforms: [...], skipped }.
/// Single-clip playback: the clip's value replaces the component; channels whose MustContribute
/// is false keep the base value (XAnimInstance::WriteFields 0x11ec4e0).
export function bindClip(anim, clip) {
  const byTarget = new Map(), uniforms = [];
  let skipped = 0;
  for (const ch of clip.channels) {
    if (!ch.mustContribute) continue;
    const a = anim.attrs[ch.attr];
    if (a.kind === KIND_UNIFORM) { uniforms.push({ material: a.target, path: a.path, sub: a.sub, curve: new Curve(ch) }); continue; }
    if (a.kind !== KIND_CORE || !CORE_CLASSES[a.core] || a.core === 5) { skipped++; continue; }
    const core = CORE_CLASSES[a.core];
    let tr = byTarget.get(a.target);
    if (!tr) byTarget.set(a.target, tr = { target: a.target, core, fields: {} });
    const name = coreFieldName(core, a.field);
    (tr.fields[name] ||= {})[a.sub] = new Curve(ch);
  }
  return { tracks: [...byTarget.values()], uniforms, skipped };
}

/// Local matrices of the bound tracks at time t (seconds): Map target -> 4x3 rows.
export function poseAt(bound, baseCores, t) {
  const out = new Map();
  for (const tr of bound.tracks) {
    const base = baseCores[tr.target];
    if (!base) continue;
    const f = {};
    for (const k of Object.keys(base)) f[k] = Array.isArray(base[k]) ? base[k].slice() : base[k];
    for (const [name, comps] of Object.entries(tr.fields)) {
      if (!f[name]) continue;
      for (const [sub, curve] of Object.entries(comps)) f[name][sub] = curve.evaluate(t);
    }
    out.set(tr.target, localMatrix(tr.core, f));
  }
  return out;
}
